// app.routes.ts
import { Routes } from '@angular/router';
import {
  canActivate,
  redirectLoggedInTo,
  redirectUnauthorizedTo,
} from '@angular/fire/auth-guard';
import { RegisterPage } from './pages/auth/register/register.page';
import { LoginPage } from './pages/auth/login/login.page';
import { hasCustomClaimRedirect, isOnboarded } from './util/AuthGuard';
import { HomePage } from './pages/dashboard/home/home.page';
import { SynergiesComponent } from './pages/dashboard/synergies/synergies.component';
import { OnboardingPage } from './pages/auth/onboarding/onboarding.page';
import { PendingApprovalPage } from './pages/pending-approval/pending-approval.page';

const redirectLoggedInToDashboard = () => redirectLoggedInTo(['/dashboard']);
const redirectUnauthorizedToLogin = () => redirectUnauthorizedTo(['/auth/login']);

// Check if user is onboarded AND verified
const isOnboardedAndVerified = () => isOnboarded('regional_verified', '/auth/login', '/auth/onboarding');
const isAdmin = () => hasCustomClaimRedirect(['regional_admin', 'central_admin'], '/dashboard');

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    path: 'dashboard',
    loadComponent: () => import('./pages/dashboard/dashboard.page').then(m => m.DashboardPage),
    ...canActivate(isOnboardedAndVerified), // ✅ Must be verified to access dashboard
    children: [
      {
        path: 'gemeindeblatt',
        loadComponent: () => import('./pages/dashboard/gemeindeblatt/gemeindeblatt.page').then(m => m.GemeindeblattPage)
      },
      {
        path: 'intelligence',
        loadComponent: () => import('./pages/dashboard/intelligence/intelligence.page').then(m => m.IntelligencePage)
      },

      {
        path: 'elearning',
        loadComponent: () => import('./pages/dashboard/elearning/elearning.page').then(m => m.ElearningPage)
      },
      {
        path: 'elearning/:id',
        loadComponent: () => import('./pages/dashboard/elearning/elearning-detail/elearning-detail.page').then(m => m.ElearningDetailPage)
      },
      {
        path: '',
        title: 'Dashboard',
        component: HomePage,
      },
      {
        path: 'partners',
        title: "Smart City Partner",
        loadComponent: () => import('./pages/dashboard/partners/partners.page').then(m => m.PartnersPage)
      },
      {
        path: 'partners/:id',
        title: "Smart City Partner",
        loadComponent: () => import('./pages/dashboard/partners/partner-detail/partner-detail.component').then(m => m.PartnerDetailComponent)
      },
      {
        path: 'synergies',
        title: "Smart City Synergies",
        component: SynergiesComponent
      },
      // {
      //   path: 'ethik',
      //   title: 'Ethikrat',
      //   loadComponent: () => import('./pages/dashboard/ethik/ethik.page').then(m => m.EthikPage)
      // },
      // {
      //   path: 'pools',
      //   title: 'Investmentpools',
      //   loadComponent: () => import('./pages/dashboard/pools/pools.page').then(m => m.PoolsPage)
      // },
      {
        path: 'pools/:id',
        title: 'Investmentpools',
        loadComponent: () => import('./pages/dashboard/pools/pool-detail/pool-detail.page').then(m => m.PoolDetailPage)
      },
      {
        path: 'csrd',
        title: 'CSRD Reporting',
        loadComponent: () => import('./pages/dashboard/csrd/csrd.page').then(m => m.CsrdPage)
      },
      {
        path: 'csrd/create',
        title: 'CSRD | Neuer Report',
        loadComponent: () => import('./pages/dashboard/csrd/create-csrd/create-csrd.page').then(m => m.CreateCsrdPage)
      },
      {
        path: 'feed',
        title: 'Feed',
        loadComponent: () => import('./pages/dashboard/feed/feed.page').then(m => m.FeedPage)
      }, {
        path: 'feed/stats',
        loadComponent: () => import('./pages/dashboard/feed/stats-dashboard/stats-dashboard.page').then(m => m.StatsDashboardPage)
      },
      {
        path: 'feed/new',
        title: 'Neuer Beitrag',
        loadComponent: () => import('./pages/dashboard/feed/new-post/new-post.page').then(m => m.NewPostPage)
      },
      {
        path: 'governance',
        title: 'Governance',
        loadComponent: () => import('./pages/dashboard/governance/governance.page').then(m => m.GovernancePageComponent)
      },

      {
        path: 'products',
        title: 'Produktverwaltung',
        loadComponent: () => import('./pages/dashboard/products/products.page').then(m => m.ProductsPage)
      },
      {
        path: 'settings',
        title: 'Einstellungen',
        loadComponent: () => import('./pages/dashboard/settings/settings.page').then(m => m.SettingsPage)
      },
      {
        path: 'admin/users',
        title: 'Benutzerverwaltung',
        loadComponent: () => import('./pages/admin/admin-users/admin-users.page').then(m => m.AdminUsersPage),
        ...canActivate(isAdmin)
      },
      {
        path: 'admin/invitations',
        title: 'Einladungen',
        loadComponent: () => import('./pages/admin/admin-invitations/admin-invitations.page').then(m => m.AdminInvitationsPage),
        ...canActivate(isAdmin)
      },
      {
        path: 'admin/elearning',
        loadComponent: () => import('./pages/admin/admin-elearning/admin-elearning.page').then(m => m.AdminElearningPage),
        ...canActivate(isAdmin)
      },
      {
        path: 'admin/elearning/:id',
        loadComponent: () => import('./pages/admin/admin-elearning/elearning-builder/elearning-builder.page').then(m => m.ElearningBuilderPage),
        ...canActivate(isAdmin)
      },
      {
        path: 'admin/ethikrat',
        loadComponent: () => import('./pages/admin/admin-ethikrat/applications.page').then(m => m.EthikratApplicationsPage),
        ...canActivate(isAdmin)
      },
      {
        path: 'admin/ethikrat/applications/:id',
        loadComponent: () => import('./pages/admin/admin-ethikrat/application-detail/application-detail.page').then(m => m.ApplicationDetailPage),
        ...canActivate(isAdmin)
      },
      // ESG Routes
      {
        path: 'esg/environment',
        title: 'Environment Score',
        loadComponent: () => import('./pages/esg/environment/environment.page').then(m => m.EnvironmentPage)
      },
      {
        path: 'esg/environment/project/:id',
        title: 'Projekt Details',
        loadComponent: () => import('./pages/esg/environment/project-detail/project-detail.page').then(m => m.ProjectDetailPage)
      },
      {
        path: 'esg/social',
        title: 'Social Score',
        loadComponent: () => import('./pages/esg/social/social.page').then(m => m.SocialPage)
      },
      {
        path: 'esg/governance',
        title: 'Governance Score',
        loadComponent: () => import('./pages/esg/governance/governance.page').then(m => m.GovernancePage)
      }
    ]
  },
  {
    path: 'auth',
    children: [
      {
        path: 'login',
        component: LoginPage,
        ...canActivate(redirectLoggedInToDashboard),
      },
      {
        path: 'register',
        component: RegisterPage,
        ...canActivate(redirectLoggedInToDashboard),
      },
      {
        path: 'onboarding',
        component: OnboardingPage,
        ...canActivate(redirectUnauthorizedToLogin),
      },
      {
        path: 'pending-approval',
        component: PendingApprovalPage,
        ...canActivate(redirectUnauthorizedToLogin),
      },
    ],
  },
  {
    path: 'agbs',
    loadComponent: () => import('./pages/agbs/agbs.page').then(m => m.AgbsPage)
  },
  {
    path: 'invitation/accept/:token',
    title: 'Einladung annehmen',
    loadComponent: () => import('./pages/invitation-accept/invitation-accept.page').then(m => m.InvitationAcceptPage)
  }
];